import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Typography,
    TypographyProps,
    alpha
} from "@mui/material";
import {
    Dispatch,
    SetStateAction,
    useEffect,
    useState
} from "react";
import {
    PermissionResourceType,
    Property
} from "../../models";

import ArchiveIcon from '@mui/icons-material/Archive';
import { AttachmentImageCarousel } from "../image/AttachmentImageCarousel";
import DownloadIcon from '@mui/icons-material/GetApp';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { OrganizationType } from "../../models";
import { TemporaryImage } from "../../componentsV2/image/TemporaryImage";
import { UploadStagingImageCarousel } from "../image/UploadStagingImageCarousel";
import { propertyIdToPropertySelectorFamily } from "../../lib/property/state/PropertyRecoilState";
import theme from "../../assets/style/theme";
import { useHistory } from "react-router-dom";
import { useRecoilCallback } from "recoil";
import { useUser } from "../../components/user/UserContext";

export interface PropertyCardProps {
    propertyId?: string;
    readonly isAvailable?: boolean;
    readonly isRecentlyCreated?: boolean;
    readonly onClick?: CallableFunction;
    property?: Property;
    images?: TemporaryImage[];
    setImages?: Dispatch<SetStateAction<TemporaryImage[]>>;
    textAlignment?: TypographyProps["textAlign"];
}

const PropertyCard = (props: PropertyCardProps) => {
    /* Internal page states */
    const { isAvailable = true, isRecentlyCreated = false, onClick, images, setImages, textAlignment = "start" } = props;
    let { propertyId = null } = props;
    const { accountType } = useUser();

    const [property, setProperty] = useState<Property | null>(props.property ?? null);

    const fetchProperty = useRecoilCallback(({ snapshot }) => (propertyId: string) => (
        snapshot.getPromise(propertyIdToPropertySelectorFamily(propertyId)).then((property) => setProperty(property))
    ), []);

    useEffect(() => {
        if (!property && propertyId) {
            fetchProperty(propertyId);
        }
    }, [property, propertyId]);

    /* Dependencies */
    const history = useHistory();

    const navigate = () => {
        switch (accountType) {
            case OrganizationType.PROPERTY_MANAGEMENT:
                history.push(`/properties/${propertyId}/dashboard`);
                break;
            default:
                history.push(`/properties/${propertyId}/issues`);
                break;
        }
    };

    return (
        <Card
            sx={{
                backgroundColor: !property?.isArchived ? alpha(theme.palette.primary.light, 0.20) : alpha(theme.palette.grey[600], 0.20),
                border: "lightgray 1px solid",
                borderRadius: "0px",
                boxShadow: "none",
            }}
        >
            <CardActionArea onClick={() => {
                if (!propertyId) {
                    return;
                }
                if (onClick) {
                    return onClick()
                } else {
                    return navigate();
                }
            }} component="span">
                <CardMedia
                    sx={{
                        p: 1,
                        position: "relative",
                    }}
                >
                    <Box
                        sx={{
                            aspectRatio: "16/9",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        {images && setImages && <UploadStagingImageCarousel imageDTOs={images} setImageDTOs={setImages} />}
                        {!images && <AttachmentImageCarousel
                            attachmentParent={{ id: property?.id ?? "", type: PermissionResourceType.PROPERTY }}
                            thumbnailImageCarouselConfiguration={{
                                showAddButton: false,
                                showContextMenu: false,
                                showTimestamp: false,
                                enableDropZone: false,
                                showNavigation: false,
                                showEditButton: false,
                                swiperProps: {
                                    pagination: {
                                        dynamicBullets: true,
                                    }
                                }
                            }}
                            expandable={false}
                        />
                        }
                    </Box>
                    {!isAvailable && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(128, 128, 128, 0.5)",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                zIndex: 1
                            }}
                        >
                            <DownloadIcon fontSize="large" color="action" />
                        </Box>
                    )}
                    {isRecentlyCreated && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                zIndex: 1
                            }}
                        >
                            <FileUploadIcon fontSize="large" color="info" />
                        </Box>
                    )}
                    {property?.isArchived && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                zIndex: 1,
                                opacity: 0.5
                            }}
                        >
                            <ArchiveIcon
                                sx={{
                                    fontSize: "10rem"
                                }}
                            />
                        </Box>
                    )}
                </CardMedia>
                <CardContent
                    sx={{
                        aspectRatio: "6/1",
                        py: 0,
                        px: 1
                    }}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                color={!property?.isArchived ? "primary" : "textSecondary"}
                                overflow={"hidden"}
                                textOverflow={"ellipsis"}
                                noWrap
                                textAlign={textAlignment}
                            >
                                {property?.isArchived && (
                                    <Typography
                                        component="span"
                                        variant="h6"
                                        pr={1}
                                    >
                                        (Archived) 
                                    </Typography>
                                )}
                                {property?.name}
                            </Typography>
                        </Box>
                        <Box ml={1} mb={1}>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                overflow={"hidden"}
                                textOverflow={"ellipsis"}
                                noWrap
                                textAlign={textAlignment}
                            >
                                {property?.address}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default PropertyCard;
